import { GlobalDisclaimerLevel, GlobalDisclaimerStoreValue, IGlobalDisclaimerStoreAddOptions } from '../models/disclaimers';
import { QueryStore } from './QueryStore';

export class GlobalDisclaimerStore {
	lastSuperscript: number = 0;
	values: GlobalDisclaimerStoreValue[] = [];
	subscribers: Function[] = [];
	subscribe(subscriber: () => void) {
		if (!this.subscribers) {
			this.subscribers = [];
		}
		this.subscribers.push(subscriber);
	}
	publish() {
		if (this.subscribers && this.subscribers.length > 0) {
			this.subscribers.forEach((subscriber) => {
				if (subscriber && typeof subscriber === 'function') {
					subscriber();
				}
			});
		}
	}
	add(appKey: string, responseContext: any, queryStore: QueryStore, ...optionsParams: IGlobalDisclaimerStoreAddOptions[]): GlobalDisclaimerStoreValue[] {
		const response: Set<GlobalDisclaimerStoreValue> = new Set();
		if (!this.values) {
			this.values = [];
		}
		let hasChange = false;
		const overrideDefined = optionsParams.some(option => option.overrideOptions);
		if (overrideDefined) {
			// Get the override options to the end of the options
			// Since it is not specified in the requirements we will have the overridden superscripts at the end
			// We can enable a config option in the overrideOptions to set the position
			optionsParams = this.sortOptionsParams(optionsParams);
		}
		for (const options of optionsParams) {
			// Add text logic in here too
			const { disclaimerType, textQuery = {} } = options;
			if (textQuery) {
				const text = GlobalDisclaimerStoreValue.getText(
					queryStore,
					disclaimerType,
					textQuery,
					responseContext
				);
				// SETUP NEW COMPOSITE KEY WITH TYPE AND TEXT
				if (text && text.length > 0) {
					const compositeKey = `${disclaimerType}_${text}`;

					const existing = this.values.find(value => value.key === compositeKey);
					if (existing) {
						existing.addApp(appKey, options);
						response.add(existing);
					}
					else {
						const nextSuperscript = options.overrideOptions ? options.overrideOptions.character : ++this.lastSuperscript;
						const value = new GlobalDisclaimerStoreValue(
							queryStore,
							nextSuperscript,
							compositeKey,
							responseContext,
							appKey,
							options
						);
						this.values.push(
							value,
						);
						response.add(value);
					}
					if (this.values.length > 0) {
						hasChange = true;
					}
				}
			}
		}
		// Order here is important otherwise the reorder would not fire
		hasChange = this.reorder() || hasChange;
		if (hasChange) {
			this.publish();
		}
		return Array.from(response);
	}
	sortOptionsParams(optionsParams: IGlobalDisclaimerStoreAddOptions[]) {
		return optionsParams.sort((a, b) => {
			// Sort the overridden options to the front
			return (!a.overrideOptions && b.overrideOptions) ? -1 : (a.overrideOptions && !b.overrideOptions) ? 1 : 0;
		});
	}
	get(level: GlobalDisclaimerLevel, appKey?: string) {
		return this.values.filter(value => value.matches(level, appKey)).sort((a: GlobalDisclaimerStoreValue, b: GlobalDisclaimerStoreValue) => {
			if (typeof a.superscript === 'number' && typeof b.superscript === 'number') {
				return a.superscript - b.superscript;
			}
			if (typeof a.superscript === 'number') {
				return -1;
			}
			if (typeof b.superscript === 'number') {
				return 1;
			}
			return 0;
		});
	}
	clear() {
		this.values = [];
		this.publish();
	}
	reorder() {
		let hasChange = false;
		const ordersDefined = this.values.some(v => v.order !== GlobalDisclaimerStoreValue.maxOrder);
		if (ordersDefined) {
			const currentOrder = this.values.map(v => v.superscript);
			this.values = this.values.sort((a, b) => {
				if (a.order === b.order) {
					return (a.superscript < b.superscript) ? -1 : (a.superscript > b.superscript) ? 1 : 0;
				}
				return (a.order < b.order) ? -1 : 1;
			});
			const newOrder = this.values.map(v => v.superscript);
			for (let i = 0; i < currentOrder.length; i++) {
				const oldValue = currentOrder[i];
				const newValue = newOrder[i];
				if (oldValue !== newValue) {
					hasChange = true;
					break;
				}
			}
			if (hasChange) {
				let lastSuperscript = 0;
				this.values.forEach(value => {
					if (typeof value.superscript === 'number') {
						value.superscript = ++lastSuperscript;
					}
				});
			}
		}
		return hasChange;
	}
}